/**
 * Returns a promise that resolves after a specified number of milliseconds,
 * along with a skip function to abort the wait.
 *
 * @param ms - The number of milliseconds to wait
 * @returns An object with a wait promise and skipWait function
 *
 * @example
 *   const { wait, skipWait } = skippableWait(1000);
 *   // Later, if you need to skip:
 *   skipWait();
 *   await wait; // Will resolve immediately when skipped
 */
export function skippableWait(ms: number): {
  wait: Promise<void>;
  skipWait: () => void;
} {
  let timeoutId: number | undefined;
  let resolvePromise: (() => void) | undefined;
  let isResolved = false;

  const cleanup = () => {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
      timeoutId = undefined;
    }

    resolvePromise = undefined;
    isResolved = true;
  };

  const wait = new Promise<void>((resolve) => {
    if (isResolved) {
      resolve();
      return;
    }

    resolvePromise = () => {
      if (!isResolved) {
        resolve();
        cleanup();
      }
    };

    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line unicorn/prefer-global-this
    timeoutId = window.setTimeout(() => {
      if (!isResolved) {
        resolve();
        cleanup();
      }
    }, ms);
  });

  const skipWait = () => {
    if (!isResolved && resolvePromise) {
      resolvePromise();
    }
  };

  return { wait, skipWait };
}
